/**
 * Referee Balance Log Contracts
 *
 * DATABASE-FIRST: Matches referee_balance_log table
 * Source: MCP query on 2026-01-02
 *
 * Table: referee_balance_log (11 columns)
 *   id: integer (PK, serial)
 *   referee_identifier: varchar NOT NULL
 *   event_type: varchar NOT NULL (CHECK: 'user_deposit' | 'user_withdrawal')
 *   reference_number: varchar NOT NULL
 *   user_telegram_id: bigint NOT NULL
 *   transaction_amount: numeric NOT NULL
 *   usdt_amount: numeric (nullable)
 *   commission_percent: numeric NOT NULL
 *   commission_amount: numeric NOT NULL
 *   is_earning: boolean NOT NULL
 *   created_at: timestamptz DEFAULT now()
 */
import { z } from 'zod';
import { timestampStringRequired, successResponseSchema } from '../common.js';
// =============================================================================
// EVENT TYPE (CHECK constraint)
// =============================================================================
/**
 * Valid event types from CHECK constraint 'valid_event_type'
 * CHECK ((event_type)::text = ANY (ARRAY['user_deposit', 'user_withdrawal']))
 */
export const refereeEventTypeValues = ['user_deposit', 'user_withdrawal'];
export const refereeEventTypeSchema = z.enum(refereeEventTypeValues);
// =============================================================================
// BALANCE LOG ENTRY
// =============================================================================
/**
 * Single balance log entry
 * Matches referee_balance_log table columns + backend transformation
 * Joined with users table for user info display
 *
 * NOTE: user_telegram_id is returned as string (backend: .toString())
 * NOTE: All numeric fields are returned as numbers (backend: ::float cast)
 */
export const refereeBalanceLogEntrySchema = z.object({
    id: z.number().int().positive(),
    eventType: refereeEventTypeSchema,
    referenceNumber: z.string(),
    userTelegramId: z.string(), // bigint → string for JSON safety
    // User info from JOIN (nullable - user may not exist)
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    transactionAmount: z.number(),
    usdtAmount: z.number().nullable(),
    commissionPercent: z.number().min(0).max(100),
    commissionAmount: z.number(),
    isEarning: z.boolean(),
    createdAt: timestampStringRequired,
});
// =============================================================================
// API RESPONSE
// =============================================================================
/**
 * GET /api/referee/balance-log response data (inner data, for frontend consumption)
 */
export const refereeBalanceLogResponseDataSchema = z.object({
    logs: z.array(refereeBalanceLogEntrySchema),
    total: z.number().int().nonnegative(),
});
/**
 * GET /api/referee/balance-log full response (with success wrapper)
 */
export const refereeBalanceLogResponseSchema = successResponseSchema(refereeBalanceLogResponseDataSchema);
// =============================================================================
// REQUEST (query params)
// =============================================================================
/**
 * Balance log query parameters
 */
export const refereeBalanceLogQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
//# sourceMappingURL=balance-log.js.map